import React, { useState, useEffect } from 'react';
import { serviceAPI, taskTypeAPI } from '../api/api';
import { Plus, Edit2, Save, X, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

const Masters = () => {
  const [activeTab, setActiveTab] = useState('services');
  const [services, setServices] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Service form
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [serviceForm, setServiceForm] = useState({ name: '', description: '' });
  
  // Task Type form
  const [showTaskTypeForm, setShowTaskTypeForm] = useState(false);
  const [taskTypeForm, setTaskTypeForm] = useState({
    service_id: '',
    name: '',
    description: '',
    sub_tasks_template: [],
  });
  const [subTaskInput, setSubTaskInput] = useState('');
  
  // Subtask management for existing task types
  const [expandedTaskType, setExpandedTaskType] = useState(null);
  const [newSubtaskInput, setNewSubtaskInput] = useState('');
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [editSubtaskTitle, setEditSubtaskTitle] = useState('');

  useEffect(() => {
    fetchServices();
    fetchTaskTypes();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await serviceAPI.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchTaskTypes = async () => {
    try {
      const response = await taskTypeAPI.getAll();
      setTaskTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch task types:', error);
    }
  };

  const handleCreateService = async () => {
    if (!serviceForm.name) return;
    setLoading(true);
    try {
      await serviceAPI.create(serviceForm);
      setServiceForm({ name: '', description: '' });
      setShowServiceForm(false);
      fetchServices();
    } catch (error) {
      alert('Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubTask = () => {
    if (!subTaskInput.trim()) return;
    const newSubTasks = [...taskTypeForm.sub_tasks_template, {
      title: subTaskInput,
      order: taskTypeForm.sub_tasks_template.length + 1,
    }];
    setTaskTypeForm({ ...taskTypeForm, sub_tasks_template: newSubTasks });
    setSubTaskInput('');
  };

  const handleRemoveSubTask = (index) => {
    const newSubTasks = taskTypeForm.sub_tasks_template.filter((_, i) => i !== index);
    setTaskTypeForm({ ...taskTypeForm, sub_tasks_template: newSubTasks });
  };

  const handleCreateTaskType = async () => {
    if (!taskTypeForm.service_id || !taskTypeForm.name) {
      alert('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await taskTypeAPI.create(taskTypeForm);
      setTaskTypeForm({
        service_id: '',
        name: '',
        description: '',
        sub_tasks_template: [],
      });
      setShowTaskTypeForm(false);
      fetchTaskTypes();
    } catch (error) {
      alert('Failed to create task type');
    } finally {
      setLoading(false);
    }
  };

  // Subtask management for existing task types
  const handleAddSubtaskToTaskType = async (taskTypeId) => {
    if (!newSubtaskInput.trim()) return;
    try {
      await taskTypeAPI.addSubtask(taskTypeId, newSubtaskInput);
      setNewSubtaskInput('');
      fetchTaskTypes();
    } catch (error) {
      alert('Failed to add subtask');
    }
  };

  const handleUpdateSubtask = async (taskTypeId, subtaskId) => {
    if (!editSubtaskTitle.trim()) return;
    try {
      await taskTypeAPI.updateSubtask(taskTypeId, subtaskId, editSubtaskTitle);
      setEditingSubtask(null);
      setEditSubtaskTitle('');
      fetchTaskTypes();
    } catch (error) {
      alert('Failed to update subtask');
    }
  };

  const handleDeleteSubtask = async (taskTypeId, subtaskId) => {
    if (!window.confirm('Are you sure you want to delete this subtask?')) return;
    try {
      await taskTypeAPI.deleteSubtask(taskTypeId, subtaskId);
      fetchTaskTypes();
    } catch (error) {
      alert('Failed to delete subtask');
    }
  };

  const startEditSubtask = (taskTypeId, subtask) => {
    setEditingSubtask({ taskTypeId, subtaskId: subtask.id });
    setEditSubtaskTitle(subtask.title);
  };

  return (
    <div className="space-y-6">
      <div className="flex space-x-4 border-b">
        <button
          onClick={() => setActiveTab('services')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'services'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="services-tab"
        >
          Services
        </button>
        <button
          onClick={() => setActiveTab('task-types')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'task-types'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="task-types-tab"
        >
          SUB SERVICES
        </button>
      </div>

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Services</h2>
            <button
              onClick={() => setShowServiceForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              data-testid="create-service-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </button>
          </div>

          {showServiceForm && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Service Name *</label>
                  <input
                    type="text"
                    value={serviceForm.name}
                    onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., GST, TDS, Income Tax"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="3"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateService}
                    disabled={loading}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={() => setShowServiceForm(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => (
              <div key={service.id} className="bg-white p-4 rounded-lg shadow border">
                <h3 className="font-semibold text-lg">{service.name}</h3>
                {service.description && (
                  <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUB SERVICES Tab */}
      {activeTab === 'task-types' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">SUB SERVICES</h2>
            <button
              onClick={() => setShowTaskTypeForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              data-testid="create-task-type-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Sub Service
            </button>
          </div>

          {showTaskTypeForm && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Service *</label>
                  <select
                    value={taskTypeForm.service_id}
                    onChange={(e) => setTaskTypeForm({ ...taskTypeForm, service_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select Service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sub Service Name *</label>
                  <input
                    type="text"
                    value={taskTypeForm.name}
                    onChange={(e) => setTaskTypeForm({ ...taskTypeForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., GSTR-3B Filing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={taskTypeForm.description}
                    onChange={(e) => setTaskTypeForm({ ...taskTypeForm, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Sub-tasks Template</label>
                  <div className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={subTaskInput}
                      onChange={(e) => setSubTaskInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSubTask()}
                      className="flex-1 px-3 py-2 border rounded-lg"
                      placeholder="Enter sub-task and press Enter"
                    />
                    <button
                      onClick={handleAddSubTask}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {taskTypeForm.sub_tasks_template.map((subTask, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">
                          {index + 1}. {subTask.title}
                        </span>
                        <button
                          onClick={() => handleRemoveSubTask(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateTaskType}
                    disabled={loading}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={() => setShowTaskTypeForm(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {taskTypes.map((taskType) => (
              <div key={taskType.id} className="bg-white p-4 rounded-lg shadow border">
                <div 
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => setExpandedTaskType(expandedTaskType === taskType.id ? null : taskType.id)}
                >
                  <div className="flex items-center gap-2">
                    {expandedTaskType === taskType.id ? 
                      <ChevronDown className="w-5 h-5 text-gray-500" /> : 
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    }
                    <div>
                      <h3 className="font-semibold text-lg">{taskType.name}</h3>
                      <p className="text-sm text-gray-500">Service: {taskType.service_name}</p>
                      {taskType.description && (
                        <p className="text-sm text-gray-600 mt-1">{taskType.description}</p>
                      )}
                    </div>
                  </div>
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                    {(taskType.sub_tasks_template || []).length} sub-tasks
                  </span>
                </div>
                
                {/* Expanded Subtasks Management */}
                {expandedTaskType === taskType.id && (
                  <div className="mt-4 border-t pt-4">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-sm font-medium text-gray-700">Sub-tasks Template:</p>
                    </div>
                    
                    {/* Add new subtask */}
                    <div className="flex space-x-2 mb-4">
                      <input
                        type="text"
                        value={newSubtaskInput}
                        onChange={(e) => setNewSubtaskInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddSubtaskToTaskType(taskType.id)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        placeholder="Enter new sub-task and press Enter"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSubtaskToTaskType(taskType.id);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Subtasks list */}
                    <div className="space-y-2">
                      {(taskType.sub_tasks_template || []).map((subTask, index) => (
                        <div key={subTask.id || index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          {editingSubtask?.taskTypeId === taskType.id && editingSubtask?.subtaskId === subTask.id ? (
                            <div className="flex-1 flex items-center space-x-2">
                              <input
                                type="text"
                                value={editSubtaskTitle}
                                onChange={(e) => setEditSubtaskTitle(e.target.value)}
                                className="flex-1 px-2 py-1 border rounded text-sm"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateSubtask(taskType.id, subTask.id);
                                }}
                                className="text-green-600 hover:text-green-800"
                              >
                                <Save className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingSubtask(null);
                                }}
                                className="text-gray-600 hover:text-gray-800"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="text-sm">
                                {index + 1}. {subTask.title}
                              </span>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditSubtask(taskType.id, subTask);
                                  }}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSubtask(taskType.id, subTask.id);
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                      {(!taskType.sub_tasks_template || taskType.sub_tasks_template.length === 0) && (
                        <p className="text-sm text-gray-500 italic">No sub-tasks defined. Add one above.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Masters;
